// Substitui window.alert/window.confirm (popups nativos do navegador, feios e
// fora do padrão visual do sistema) por um toast e um modal de confirmação no
// mesmo estilo do resto do app.
//
// Uso:
//   const toast = useToast();      toast.success('Salvo!'); toast.error('Falhou.');
//   const confirm = useConfirm();  if (!(await confirm('Apagar isso?'))) return;
//
// <FeedbackProvider> precisa envolver a árvore inteira (ver App.js) — os hooks
// abaixo lançam erro se usados fora dele, pra pegar esse tipo de engano cedo.
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';

const FeedbackContext = createContext(null);

const TOAST_ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info };
const TOAST_STYLES = {
  success:
    'bg-white dark:bg-gray-900 border-green-100 dark:border-green-900/50 text-green-800 dark:text-green-400 [&_svg]:text-green-500',
  error:
    'bg-white dark:bg-gray-900 border-red-100 dark:border-red-900/50 text-red-800 dark:text-red-400 [&_svg]:text-red-500',
  warning:
    'bg-white dark:bg-gray-900 border-amber-100 dark:border-amber-900/50 text-amber-800 dark:text-amber-400 [&_svg]:text-amber-500',
  info: 'bg-white dark:bg-gray-900 border-blue-100 dark:border-blue-900/50 text-blue-800 dark:text-blue-400 [&_svg]:text-blue-500',
};

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null); // { message, title, confirmText, cancelText, tone, resolve }
  const nextId = useRef(1);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, type = 'info', duration = 4500) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, type }]);
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast],
  );

  const toast = {
    show: showToast,
    success: (msg, duration) => showToast(msg, 'success', duration),
    error: (msg, duration) => showToast(msg, 'error', duration),
    warning: (msg, duration) => showToast(msg, 'warning', duration),
    info: (msg, duration) => showToast(msg, 'info', duration),
  };

  // Retorna uma função async: confirm(mensagem, opcoes) -> Promise<boolean>.
  // Também aceita confirm({ message, ...opcoes }) com segurança caso seja passado um objeto.
  // Guarda o `resolve` da Promise no estado; os botões do modal chamam ele.
  const confirm = useCallback((messageOrOptions, options = {}) => {
    let message = messageOrOptions;
    let opts = options;

    if (
      messageOrOptions &&
      typeof messageOrOptions === 'object' &&
      !React.isValidElement(messageOrOptions)
    ) {
      message = messageOrOptions.message || '';
      opts = { ...messageOrOptions, ...options };
    }

    return new Promise((resolve) => {
      setConfirmState({
        message,
        title: opts.title || 'Confirmar ação',
        confirmText: opts.confirmText || 'Confirmar',
        cancelText: opts.cancelText || 'Cancelar',
        extraText: opts.extraText || null,
        tone: opts.tone || (opts.danger ? 'danger' : 'primary'),
        resolve,
      });
    });
  }, []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  return (
    <FeedbackContext.Provider value={{ toast, confirm }}>
      {children}

      {/* Toasts empilhados no canto superior direito — abaixo do header (que
          tem 64px de altura, `h-16`, mais o sino de notificação nesse mesmo
          canto): top-20 (80px) garante que o balão não fique por cima desses
          elementos do header. */}
      <div className="fixed top-20 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
        {toasts.map((t) => {
          const Icon = TOAST_ICONS[t.type] || Info;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-start gap-2.5 p-3 rounded-2xl border shadow-lg animate-[toast-in_0.2s_ease-out] ${TOAST_STYLES[t.type] || TOAST_STYLES.info}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm flex-1 whitespace-pre-wrap">{t.message}</p>
              <button
                onClick={() => removeToast(t.id)}
                className="flex-shrink-0 w-5 h-5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Modal de confirmação */}
      <Modal
        isOpen={!!confirmState}
        onClose={() => closeConfirm(false)}
        title={confirmState?.title}
        maxWidth="sm"
      >
        {confirmState && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
              {confirmState.message}
            </p>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => closeConfirm(false)}>
                {confirmState.cancelText}
              </Button>
              {confirmState.extraText && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => closeConfirm('extra')}
                >
                  {confirmState.extraText}
                </Button>
              )}
              <Button
                type="button"
                variant={confirmState.tone === 'primary' ? 'primary' : 'danger'}
                onClick={() => closeConfirm(true)}
              >
                {confirmState.confirmText}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </FeedbackContext.Provider>
  );
}

function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx)
    throw new Error(
      'useToast/useConfirm precisam estar dentro de <FeedbackProvider> (ver App.js).',
    );
  return ctx;
}

export function useToast() {
  return useFeedback().toast;
}

export function useConfirm() {
  return useFeedback().confirm;
}
